import React from "react";
import {
  CheckboxFieldContext,
  Header as AriaHeader,
  type HeaderProps as AriaHeaderProps,
  Keyboard,
  Label,
  ListBoxItem as AriaListBoxItem,
  type ListBoxItemProps as AriaListBoxItemProps,
  MenuItem as AriaMenuItem,
  type MenuItemProps as AriaMenuItemProps,
  Provider,
  TreeItem as AriaTreeItem,
  TreeItemContent as AriaTreeItemContent,
  type TreeItemProps as AriaTreeItemProps,
} from "react-aria-components";
import { icon } from "../../icon.css.ts";
import { Check } from "../../icons/line.tsx";
import { Checkbox } from "../Checkbox.tsx";
import type {
  IconType,
  OmittedReactAriaProps,
  OmittedReactAriaPropsWithChildren,
} from "../types.ts";
import { AvatarContext } from "./AvatarContext.ts";
import { TreeExpandChevron } from "./ChevronButton.tsx";
import * as styles from "./DropdownItem.css.ts";
import { DropdownItemContext } from "./DropdownItemContext.ts";
import { TreeSelectionContext } from "./TreeSelectionContext.ts";

interface ItemProps {
  left?: React.ReactNode | IconType;
  color?: "normal" | "danger";
  secondary?: string;
  keyboardShortcut?: string | null;
  children: React.ReactNode;
  textValue?: string;
}

interface InternalItemProps extends ItemProps {
  isSelected?: boolean;
  isIndeterminate?: boolean;
}

function renderLeft(left: React.ReactNode | IconType) {
  return typeof left === "function"
    ? React.createElement(left, { className: icon({ size: "s" }) })
    : left;
}

function Item({
  left,
  secondary,
  children,
  keyboardShortcut,
  isSelected = false,
  isIndeterminate,
  textValue,
}: InternalItemProps) {
  const { size, selectionMode = "single" } = React.use(DropdownItemContext);

  return (
    <Provider
      values={[
        [
          AvatarContext,
          {
            size: "xxs",
            isInteractive: false,
            tooltip: false,
          },
        ],
      ]}
    >
      {selectionMode === "multiple" ? (
        <Provider values={[[CheckboxFieldContext, {}]]}>
          <div className={styles.checkbox}>
            <Checkbox
              isSelected={isSelected}
              isIndeterminate={isIndeterminate}
            />
          </div>
        </Provider>
      ) : null}
      {left == null ? null : (
        <div className={styles.menuItemIcon}>{renderLeft(left)}</div>
      )}
      <Label
        slot="label"
        title={textValue}
        className={styles.itemText({ size })}
      >
        {children}
      </Label>
      {secondary == null ? null : (
        <Label
          slot="description"
          className={styles.itemTextSecondary({ size })}
        >
          {secondary}
        </Label>
      )}
      {keyboardShortcut == null ? null : (
        <Keyboard className={styles.keyboardShortcut({ size })}>
          {keyboardShortcut}
        </Keyboard>
      )}
      {isSelected && selectionMode === "single" && (
        <Check className={styles.checkedIcon} />
      )}
    </Provider>
  );
}

export type DropdownItemProps = Omit<AriaMenuItemProps, OmittedReactAriaProps> &
  ItemProps & { isIndeterminate?: boolean };

export function DropdownItem({
  color,
  children,
  left,
  secondary,
  keyboardShortcut,
  isIndeterminate,
  ...props
}: DropdownItemProps) {
  const { size } = React.use(DropdownItemContext);

  return (
    <AriaMenuItem className={styles.menuItem({ size, color })} {...props}>
      <Item
        color={color}
        left={left}
        secondary={secondary}
        keyboardShortcut={keyboardShortcut}
        isIndeterminate={isIndeterminate}
      >
        {children}
      </Item>
    </AriaMenuItem>
  );
}

export interface ListBoxItemProps
  extends
    Omit<AriaListBoxItemProps, OmittedReactAriaProps>,
    Omit<ItemProps, "children"> {
  children: string;
  isIndeterminate?: boolean;

  /**
   * @deprecated use `left` instead.
   */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  ref?: React.Ref<HTMLDivElement>;
}

export const ListBoxItem: React.FC<ListBoxItemProps> = ({
  children,
  ref,
  isIndeterminate,
  ...props
}) => {
  const { size } = React.use(DropdownItemContext);

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const left = props.left ?? props.icon;
  return (
    <AriaListBoxItem
      textValue={children}
      {...props}
      className={styles.menuItem({ size })}
      ref={ref}
    >
      {({ isSelected }) => (
        <Item
          isSelected={isSelected}
          isIndeterminate={isIndeterminate}
          left={left}
          secondary={props.secondary}
          textValue={props.textValue ?? children}
        >
          {children}
        </Item>
      )}
    </AriaListBoxItem>
  );
};

export interface SectionHeaderProps extends Omit<
  AriaHeaderProps,
  OmittedReactAriaPropsWithChildren
> {
  children: React.ReactNode;
  left?: React.ReactNode | IconType;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  children,
  left,
  ...props
}) => {
  const { size } = React.use(DropdownItemContext);
  return (
    <AriaHeader {...props} className={styles.sectionHeader}>
      {left == null ? null : renderLeft(left)}
      <span className={styles.sectionHeaderText({ size })}>{children}</span>
    </AriaHeader>
  );
};

export interface TreeDropdownItemProps extends Omit<
  AriaTreeItemProps,
  OmittedReactAriaProps | "children" | "textValue"
> {
  label: string;
  textValue?: string;
  children?: React.ReactNode;
  left?: ItemProps["left"];
  secondary?: ItemProps["secondary"];
}

export const TreeDropdownItem: React.FC<TreeDropdownItemProps> = ({
  label,
  children,
  left,
  secondary,
  textValue,
  ...props
}) => {
  const { size } = React.use(DropdownItemContext);
  const treeSelection = React.use(TreeSelectionContext);

  return (
    <AriaTreeItem
      {...props}
      textValue={textValue ?? label}
      className={styles.menuItem({ size })}
    >
      <AriaTreeItemContent>
        {({ isSelected, selectionMode, isExpanded }) => (
          <>
            <TreeExpandChevron isOpen={isExpanded} />
            <Item
              isSelected={isSelected}
              isIndeterminate={
                selectionMode === "multiple" &&
                treeSelection != null &&
                props.id != null
                  ? treeSelection.getNodeState(props.id) === "indeterminate"
                  : undefined
              }
              left={left}
              secondary={secondary}
              textValue={textValue ?? label}
            >
              {label}
            </Item>
          </>
        )}
      </AriaTreeItemContent>
      {children}
    </AriaTreeItem>
  );
};
